import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  Button,
  Box,
  IconButton,
  Tooltip,
  Typography,
  Snackbar,
  Alert,
  Avatar,
  Divider,
  Paper,
  useTheme,
  alpha
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { QRCodeSVG } from 'qrcode.react';
import { 
  ContentCopy, 
  Download, 
  OpenInNew, 
  Close as CloseIcon,
  QrCode2,
  Share as ShareIcon,
  CheckCircle,
  WhatsApp,
  Telegram
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { generatePublicUrl } from '../../utils/urlUtils';
import { trackCardShare } from '../../api/analyticsApi';

// Premium Glass Modal
const PremiumDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    background: theme.palette.mode === 'dark' 
      ? 'linear-gradient(135deg, rgba(30, 30, 30, 0.95) 0%, rgba(50, 50, 70, 0.95) 100%)'
      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(240, 245, 255, 0.95) 100%)',
    backdropFilter: 'blur(20px)',
    borderRadius: '20px',
    border: theme.palette.mode === 'dark' 
      ? '1px solid rgba(255, 255, 255, 0.1)'
      : '1px solid rgba(0, 0, 0, 0.1)',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 20px 40px -12px rgba(0, 0, 0, 0.8), 0 0 0 1px rgba(255, 255, 255, 0.05)'
      : '0 20px 40px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.8)',
    maxWidth: '420px',
    width: '90vw',
    maxHeight: '85vh',
    margin: 'auto',
    overflow: 'visible'
  },
  '& .MuiBackdrop-root': {
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    backdropFilter: 'blur(8px)'
  }
}));

// Premium Action Button
const PremiumButton = styled(Button)(({ theme, variant: buttonVariant }) => ({
  borderRadius: '12px',
  padding: '10px 20px',
  fontSize: '0.9rem',
  fontWeight: 600,
  textTransform: 'none',
  minHeight: '44px',
  position: 'relative',
  overflow: 'hidden',
  boxShadow: buttonVariant === 'primary' 
    ? '0 6px 16px rgba(25, 118, 210, 0.4)'
    : '0 3px 8px rgba(0, 0, 0, 0.1)',
  background: buttonVariant === 'primary'
    ? 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)'
    : theme.palette.mode === 'dark'
      ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.12) 100%)'
      : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0.7) 100%)',
  border: buttonVariant === 'primary' 
    ? 'none'
    : theme.palette.mode === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.2)'
      : '1px solid rgba(0, 0, 0, 0.1)',
  color: buttonVariant === 'primary' 
    ? '#ffffff'
    : theme.palette.text.primary,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: buttonVariant === 'primary'
      ? '0 8px 20px rgba(25, 118, 210, 0.5)'
      : theme.palette.mode === 'dark'
        ? '0 6px 16px rgba(255, 255, 255, 0.1)'
        : '0 6px 16px rgba(0, 0, 0, 0.15)',
    background: buttonVariant === 'primary'
      ? 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)'
      : theme.palette.mode === 'dark'
        ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.12) 0%, rgba(255, 255, 255, 0.16) 100%)'
        : 'linear-gradient(135deg, rgba(255, 255, 255, 1) 0%, rgba(255, 255, 255, 0.9) 100%)'
  },
  '&:active': {
    transform: 'translateY(0px)'
  }
}));

// QR Code Container
const QRContainer = styled(Paper)(({ theme }) => ({
  background: theme.palette.mode === 'dark'
    ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)'
    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.8) 0%, rgba(255, 255, 255, 0.6) 100%)',
  backdropFilter: 'blur(20px)',
  borderRadius: '16px',
  padding: theme.spacing(2.5),
  border: theme.palette.mode === 'dark'
    ? '1px solid rgba(255, 255, 255, 0.1)'
    : '1px solid rgba(0, 0, 0, 0.05)',
  boxShadow: theme.palette.mode === 'dark'
    ? '0 6px 24px rgba(0, 0, 0, 0.3)'
    : '0 6px 24px rgba(0, 0, 0, 0.1)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1.5)
}));

const CardShare = ({ open, onClose, card }) => {
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [qrSize, setQrSize] = useState(160);
  const theme = useTheme();
  const publicUrl = generatePublicUrl(card.slug);
  
  // Responsive QR code size
  const getQRSize = () => {
    if (window.innerWidth < 480) return 120;  // Mobile - very compact
    if (window.innerWidth < 768) return 140;  // Tablet
    return 160;  // Desktop - compact for modal fit
  };

  // Update QR size on window resize
  React.useEffect(() => {
    const updateQRSize = () => setQrSize(getQRSize());
    updateQRSize(); // Set initial size
    
    window.addEventListener('resize', updateQRSize);
    return () => window.removeEventListener('resize', updateQRSize);
  }, []);

  const handleCopyUrl = async () => {
    try {
      await trackCardShare(card.slug, 'copy_link');
    } catch (error) {
      console.error('Failed to track copy link:', error);
    }
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setShowSuccess(true);
      setTimeout(() => {
        setCopied(false);
        setShowSuccess(false);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  const handleVisitLink = async () => {
    try {
      await trackCardShare(card.slug, 'visit_link');
    } catch (error) {
      console.error('Failed to track visit link:', error);
    }
    window.open(publicUrl, '_blank', 'noopener,noreferrer');
  };

  const handleWhatsAppShare = async () => {
    try {
      await trackCardShare(card.slug, 'whatsapp');
    } catch (error) {
      console.error('Failed to track WhatsApp share:', error);
    }
    const message = `Hello! Check out my digital visiting card: ${publicUrl}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
  };

  const handleTelegramShare = async () => {
    try {
      await trackCardShare(card.slug, 'telegram');
    } catch (error) {
      console.error('Failed to track Telegram share:', error);
    }
    const message = `Hello! Check out my digital visiting card: ${publicUrl}`;
    const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(publicUrl)}&text=${encodeURIComponent('Hello! Check out my digital visiting card')}`;
    window.open(telegramUrl, '_blank', 'noopener,noreferrer');
  };

  const handleDownloadQR = async () => {
    try {
      await trackCardShare(card.slug, 'download_qr');
    } catch (error) {
      console.error('Failed to track QR download:', error);
    }
    setDownloading(true);
    try {
      const svg = document.getElementById('premium-qr-code-svg');
      if (svg) {
        const svgData = new XMLSerializer().serializeToString(svg);
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        const img = new Image();
        
        img.onload = () => {
          canvas.width = img.width;
          canvas.height = img.height;
          
          // Add white background
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          
          ctx.drawImage(img, 0, 0);
          const pngFile = canvas.toDataURL("image/png");
          const downloadLink = document.createElement("a");
          downloadLink.download = `${card.slug}-qr-code.png`;
          downloadLink.href = pngFile;
          downloadLink.click();
        };
        img.src = "data:image/svg+xml;base64," + btoa(svgData);
      }
    } catch (err) {
      console.error('Failed to download QR code:', err);
    } finally {
      setTimeout(() => setDownloading(false), 1000);
    }
  };

  return (
    <>
      <PremiumDialog 
        open={open} 
        onClose={onClose}
        maxWidth="sm" 
        fullWidth
        TransitionComponent={motion.div}
        PaperProps={{
          sx: {
            height: { xs: 'auto', sm: 720 }, // A fixed, taller height for desktop
            minHeight: { xs: 680, sm: 720 }, // Ensure enough height on mobile
            maxHeight: '95vh',
            maxWidth: '500px',
            width: '95vw',
            overflow: 'hidden', // Prevent container from scrolling
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'center',
            p: 0,
            borderRadius: '24px'
          }
        }}
      >
        <DialogContent sx={{ 
          p: 0, 
          position: 'relative', 
          overflow: 'hidden', // No scrollbar on the content
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
        }}>
          {/* Close Button */}
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              zIndex: 10,
              backgroundColor: alpha(theme.palette.background.paper, 0.8),
              backdropFilter: 'blur(10px)',
              width: 36,
              height: 36,
              '&:hover': {
                backgroundColor: alpha(theme.palette.background.paper, 0.9),
                transform: 'scale(1.1)'
              }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          <Box sx={{ 
            p: { xs: 2, sm: 2.5 }, 
            pt: 4, 
            pb: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            minHeight: 'auto',
            height: '100%',
            flex: 1,
            justifyContent: 'space-between', // This will distribute the content vertically
          }}>
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Box sx={{ textAlign: 'center', mb: 1.2 }}>
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    mx: 'auto',
                    mb: 0.5,
                    background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                    boxShadow: '0 6px 16px rgba(25, 118, 210, 0.3)'
                  }}
                >
                  <ShareIcon sx={{ fontSize: 18 }} />
                </Avatar>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 700,
                    fontSize: { xs: '1.1rem', sm: '1.25rem' },
                    mb: 0.2
                  }}
                >
                  Share Portfolio
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{ 
                    fontSize: { xs: '0.85rem', sm: '0.95rem' },
                    fontWeight: 400,
                    mb: 0.5
                  }}
                >
                  Share your digital visiting card with others
                </Typography>
              </Box>
            </motion.div>

            {/* Action Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Box sx={{ display: 'flex', gap: 1, mb: 1.2 }}>
                <PremiumButton
                  variant="primary"
                  fullWidth
                  startIcon={copied ? <CheckCircle /> : <ContentCopy />}
                  onClick={handleCopyUrl}
                  disabled={copied}
                  sx={{ py: 1, fontSize: '0.95rem' }}
                >
                  {copied ? 'Copied!' : 'Copy Link'}
                </PremiumButton>
                <PremiumButton
                  variant="secondary"
                  fullWidth
                  startIcon={<OpenInNew />}
                  onClick={handleVisitLink}
                  sx={{ py: 1, fontSize: '0.95rem' }}
                >
                  Visit Link
                </PremiumButton>
              </Box>
            </motion.div>

            {/* Share Options */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
            >
              <Box sx={{ mb: 1.2 }}>
                <Typography 
                  variant="subtitle1" 
                  color="text.primary" 
                  sx={{ 
                    textAlign: 'center', 
                    mb: 1, 
                    fontSize: '1rem',
                    fontWeight: 600
                  }}
                >
                  Share via Social Media
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <PremiumButton
                    variant="secondary"
                    fullWidth
                    startIcon={<WhatsApp />}
                    onClick={handleWhatsAppShare}
                    sx={{
                      background: 'linear-gradient(135deg, #25d366 0%, #128c7e 100%)',
                      color: 'white',
                      py: 1,
                      fontSize: '0.95rem',
                      fontWeight: 600,
                      '&:hover': {
                        background: 'linear-gradient(135deg, #128c7e 0%, #075e54 100%)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 20px rgba(37, 211, 102, 0.3)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    WhatsApp
                  </PremiumButton>
                  <PremiumButton
                    variant="secondary"
                    fullWidth
                    startIcon={<Telegram />}
                    onClick={handleTelegramShare}
                    sx={{
                      background: 'linear-gradient(135deg, #0088cc 0%, #005580 100%)',
                      color: 'white',
                      py: 1,
                      fontSize: '0.95rem',
                      fontWeight: 600,
                      '&:hover': {
                        background: 'linear-gradient(135deg, #005580 0%, #003d5c 100%)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 20px rgba(0, 136, 204, 0.3)'
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    Telegram
                  </PremiumButton>
                </Box>
              </Box>
            </motion.div>

            {/* Divider */}
            <motion.div
              initial={{ opacity: 0, scaleX: 0 }}
              animate={{ opacity: 1, scaleX: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <Divider sx={{ my: 1.2, opacity: 0.6 }}>
                <Typography variant="subtitle2" color="text.primary" sx={{ px: 2, fontSize: '0.95rem', fontWeight: 600 }}>
                  QR Code
                </Typography>
              </Divider>
            </motion.div>

            {/* QR Code Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <QRContainer 
                elevation={0}
                sx={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1.5,
                  width: '100%',
                  p: 2,
                  minHeight: 260, // Increased minHeight for QR section
                  justifyContent: 'space-between',
                  flexShrink: 0
                }}
              >
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: '12px',
                    background: '#ffffff',
                    boxShadow: '0 3px 12px rgba(0,0,0,0.08)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: 140
                  }}
                >
                  <QRCodeSVG
                    id="premium-qr-code-svg"
                    value={publicUrl}
                    size={qrSize}
                    level="H"
                    includeMargin={true}
                    fgColor="#000000"
                    bgColor="#ffffff"
                  />
                </Box>
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ 
                    textAlign: 'center', 
                    fontSize: '0.9rem',
                    mt: 1
                  }}
                >
                  Scan to view portfolio instantly
                </Typography>
                <PremiumButton
                  variant="secondary"
                  startIcon={<Download />}
                  onClick={handleDownloadQR}
                  disabled={downloading}
                  sx={{ 
                    mt: 'auto', // Push to bottom of container
                    width: '180px',
                    fontSize: '1rem',
                    py: 1.2,
                    alignSelf: 'center',
                    fontWeight: 700,
                    borderRadius: '12px',
                    background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                    color: 'white',
                    boxShadow: '0 4px 16px rgba(25, 118, 210, 0.10)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 20px rgba(25, 118, 210, 0.18)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  {downloading ? 'Downloading...' : 'Save QR Code'}
                </PremiumButton>
              </QRContainer>
            </motion.div>
          </Box>
        </DialogContent>
      </PremiumDialog>

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={2000}
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowSuccess(false)} 
          severity="success" 
          variant="filled"
          sx={{ 
            borderRadius: '12px',
            fontWeight: 600
          }}
        >
          Link copied to clipboard!
        </Alert>
      </Snackbar>
    </>
  );
};

export default CardShare; 